import { useNetworkStore } from '../../stores/networkStore';
import { syncAllPending } from '../../services/sync-service';
import { toast } from 'sonner';
import { useState } from 'react';

/**
 * OfflineBanner - Shows offline status and pending sync count
 *
 * Displays:
 * - Offline state with warning
 * - Pending items count when online but unsynced
 * - Sync button for manual sync
 *
 * Handles syncing all entity types: service logs, work orders, clients, schedules
 */
export function OfflineBanner() {
  const { isOnline, pendingCount, isSyncing, setSyncing, updatePendingCount, updateLastSync } = useNetworkStore();
  const [syncing, setSyncingLocal] = useState(false);

  const handleSyncNow = async () => {
    setSyncingLocal(true);
    setSyncing(true);

    try {
      const result = await syncAllPending();
      updateLastSync();
      await updatePendingCount();

      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} item${result.synced === 1 ? '' : 's'}`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to sync ${result.failed} item${result.failed === 1 ? '' : 's'}`);
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncingLocal(false);
      setSyncing(false);
    }
  };

  // Don't show if online and no pending items
  if (isOnline && pendingCount === 0) return null;

  return (
    <div className="sticky top-0 z-50 bg-amber-100 border-b border-amber-300 px-4 py-2 text-sm">
      {/* Offline state */}
      {!isOnline && (
        <div className="flex items-center gap-2 text-amber-800">
          <span className="text-base">⚠️</span>
          <span className="flex-1">
            You're offline. Changes will sync when reconnected.
          </span>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 bg-amber-200 rounded-full text-xs font-medium">
              {pendingCount} pending
            </span>
          )}
        </div>
      )}

      {/* Online with pending items */}
      {isOnline && pendingCount > 0 && (
        <div className="flex items-center justify-between text-amber-800">
          <div className="flex items-center gap-2">
            <span className="text-base">🔄</span>
            <span>{pendingCount} item{pendingCount === 1 ? '' : 's'} pending sync...</span>
          </div>
          <button
            onClick={handleSyncNow}
            disabled={syncing || isSyncing}
            className="px-3 py-1 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors"
          >
            {syncing || isSyncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      )}
    </div>
  );
}
